import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { setAnalyticsConsent, setMarketingConsent } from "@/lib/analytics";

const STORAGE_KEY = "etap_consent";
const CONSENT_VERSION = "1.0";

const ConsentContext = createContext(null);

const DEFAULT_PREFS = { necessary: true, analytics: false, marketing: false };

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.version === CONSENT_VERSION && parsed.prefs) return parsed;
    return null;
  } catch {
    return null;
  }
}

function applyToScripts(prefs) {
  setAnalyticsConsent(!!prefs.analytics);
  setMarketingConsent(!!prefs.marketing);
}

export function ConsentProvider({ children }) {
  const [decided, setDecided] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [prefsOpen, setPrefsOpen] = useState(false);

  useEffect(() => {
    const stored = readStored();
    if (stored) {
      setPrefs(stored.prefs);
      setDecided(true);
      applyToScripts(stored.prefs);
    }
  }, []);

  const persist = useCallback((next) => {
    const record = {
      version: CONSENT_VERSION,
      prefs: next,
      timestamp: new Date().toISOString(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
      // storage unavailable, keep in memory only
    }
    setPrefs(next);
    setDecided(true);
    applyToScripts(next);
  }, []);

  const acceptAll = useCallback(() => {
    persist({ necessary: true, analytics: true, marketing: true });
    setPrefsOpen(false);
  }, [persist]);

  const rejectAll = useCallback(() => {
    persist({ necessary: true, analytics: false, marketing: false });
    setPrefsOpen(false);
  }, [persist]);

  const savePrefs = useCallback(
    (partial) => {
      persist({ necessary: true, analytics: !!partial.analytics, marketing: !!partial.marketing });
      setPrefsOpen(false);
    },
    [persist],
  );

  const openPreferences = useCallback(() => setPrefsOpen(true), []);

  const value = useMemo(
    () => ({ decided, prefs, prefsOpen, setPrefsOpen, acceptAll, rejectAll, savePrefs, openPreferences }),
    [decided, prefs, prefsOpen, acceptAll, rejectAll, savePrefs, openPreferences],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used within ConsentProvider");
  return ctx;
}
